/**
 * documentsBoardState.ts — the rules behind Admin > Documents' two panes, kept
 * apart from the drag mechanics so they can be tested on their own.
 *
 *   - A document dragged onto another category is filed there, last in that
 *     category's saved order (the board lists A–Z; drag only files).
 *   - A document dragged onto a packet is added to it as a new item (a copy of
 *     the reference; the document stays in the library). One document is in a
 *     packet at most once.
 *   - A packet's items keep their order (it is the order they are sent in):
 *     an item drags to a new place in its packet, or into another packet.
 */
import { compareTitles } from '@/components/admin/categoryBoardState';
import type { DocumentPacket, LibraryDocument, PacketEntry } from '@/services/documentLibrary.service';

/** A document's place in its category's saved order (dOrder, then id). */
export function compareSavedOrder(a: LibraryDocument, b: LibraryDocument): number {
  return a.dOrder - b.dOrder || a.documentID - b.documentID;
}

/** The library's own order on screen: A–Z by title. */
export function compareDocumentTitles(a: LibraryDocument, b: LibraryDocument): number {
  return compareTitles(a.title, b.title) || a.documentID - b.documentID;
}

/**
 * What a drop of `documentId` on category `categoryId` saves: that category's
 * documents in their saved order, the moved one last. Null when it is already
 * filed there.
 */
export function documentMoveSave(
  documents: LibraryDocument[],
  documentId: number,
  categoryId: number,
): { categoryId: number; ids: number[] } | null {
  const moved = documents.find((d) => d.documentID === documentId);
  if (!moved || moved.categoryID === categoryId) return null;
  const ids = documents
    .filter((d) => d.categoryID === categoryId && d.documentID !== documentId)
    .sort(compareSavedOrder)
    .map((d) => d.documentID);
  return { categoryId, ids: [...ids, documentId] };
}

/** The packets that list each document (deleted packets included), A–Z. */
export function packetsByDocument(packets: DocumentPacket[]): Map<number, DocumentPacket[]> {
  const byDocument = new Map<number, DocumentPacket[]>();
  for (const packet of packets) {
    const seen = new Set<number>();
    for (const entry of packet.entries) {
      if (entry.documentType !== 0 || entry.documentID <= 0 || seen.has(entry.documentID)) continue;
      seen.add(entry.documentID);
      const list = byDocument.get(entry.documentID) ?? [];
      list.push(packet);
      byDocument.set(entry.documentID, list);
    }
  }
  for (const list of byDocument.values()) list.sort((a, b) => compareTitles(a.name, b.name) || a.documentPacketID - b.documentPacketID);
  return byDocument;
}

/** The packet item already pointing at this document, if any. */
export function entryForDocument(packet: DocumentPacket, documentId: number): PacketEntry | undefined {
  return packet.entries.find((e) => e.documentType === 0 && e.documentID === documentId);
}

/** An item's label, as legacy's packetEntryLabel(): the document's title, or which procedure slot it fills. */
export function packetEntryLabel(entry: PacketEntry, documentsById: Map<number, LibraryDocument>): string {
  if (entry.documentType === 1) return `Procedure slot ${entry.packetWcId}`;
  const title = documentsById.get(entry.documentID)?.title.trim() ?? '';
  return title !== '' ? title : 'This document is no longer available';
}

export interface EntryMove {
  /** The packets with the item moved. */
  packets: DocumentPacket[];
  /** The target packet's items in their new order. */
  ids: number[];
  /** Set when the item changed packet: every listed item is filed in it. */
  packetId?: number;
}

/**
 * Move item `entryId` to position `index` of packet `targetPacketId` (the end
 * when index is past it). Null when nothing changes, or when the target packet
 * already has the same document.
 */
export function moveEntry(packets: DocumentPacket[], entryId: number, targetPacketId: number, index: number): EntryMove | null {
  const source = packets.find((p) => p.entries.some((e) => e.documentPacketEntryID === entryId));
  const target = packets.find((p) => p.documentPacketID === targetPacketId);
  if (!source || !target) return null;
  const entry = source.entries.find((e) => e.documentPacketEntryID === entryId) as PacketEntry;
  const crossing = source.documentPacketID !== target.documentPacketID;
  if (crossing && entry.documentType === 0 && entryForDocument(target, entry.documentID)) return null;

  const remaining = target.entries.filter((e) => e.documentPacketEntryID !== entryId);
  const at = Math.max(0, Math.min(index, remaining.length));
  const ordered = [...remaining.slice(0, at), { ...entry, documentPacketID: targetPacketId }, ...remaining.slice(at)];
  const ids = ordered.map((e) => e.documentPacketEntryID);
  if (!crossing && ids.every((id, i) => id === target.entries[i]?.documentPacketEntryID)) return null;

  const renumbered = ordered.map((e, i) => ({ ...e, orderID: i + 1 }));
  return {
    packets: packets.map((p) => {
      if (p.documentPacketID === targetPacketId) return { ...p, entries: renumbered };
      if (crossing && p.documentPacketID === source.documentPacketID) {
        return { ...p, entries: p.entries.filter((e) => e.documentPacketEntryID !== entryId) };
      }
      return p;
    }),
    ids,
    ...(crossing ? { packetId: targetPacketId } : {}),
  };
}
